import { UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";
import { STFF } from "@/models/stff.tsx";
import { FiUser } from "react-icons/fi";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import TextInput from "@/components/shared/forms/TextInput";
import Select from "@/components/shared/forms/Select";
import DateInput from "@/components/shared/forms/DateInput";
import ReadOnlyDateInput from "@/components/shared/forms/ReadOnlyDateInput";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import { useCoreData } from "@/queries.ts";
import { FiUsers } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useState, useEffect } from "react";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";
import { useSTFFFormLogic } from "../../hooks/useSTFFFormLogic";
import { useWatch } from "react-hook-form";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";
import { validateMinDate } from "@/utils/util";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";

type LockedFields = {
  staffLocalId: boolean;
  firstName: boolean;
  lastName: boolean;
  dateOfBirth: boolean;
};

type Option = {
  Text: string;
  Value: string;
};

type MultiSelectFieldProps = {
  id?: string;
  disabled: boolean;
  options: Option[];
  initialValues: string[];
  onChangeValues: (values: string[]) => void;
  maxSelections: number;
  placeholder: string;
  resetKey: string;
  errorMessage?: string;
  afterChange?: () => void;
  ariaLabel?: string;
  ariaDescribedBy?: string;
};

function MultiSelectField({
  id,
  disabled,
  options,
  initialValues,
  onChangeValues,
  maxSelections,
  placeholder,
  resetKey,
  errorMessage,
  afterChange,
  ariaLabel,
  ariaDescribedBy,
}: MultiSelectFieldProps) {
  const [selected, setSelected] = useState<Option[]>(() => {
    const initialSet = initialValues.filter(Boolean);
    return initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is Option => !!o)
      .slice(0, maxSelections);
  });

  useEffect(() => {
    const initialSet = initialValues.filter(Boolean);
    const mapped = initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is Option => !!o)
      .slice(0, maxSelections);

    setSelected(mapped);
  }, [resetKey, initialValues, options, maxSelections]);

  const handleChange = (e: MultiSelectChangeEvent) => {
    const next = (e.value as Option[]) ?? [];
    const limited = next.slice(0, maxSelections);

    setSelected(limited);
    onChangeValues(limited.map((o) => o.Value));
    afterChange?.();
  };

  useMultiSelectChipListLabel(id, ariaLabel, selected);

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // option list without having to type first.
  const [opened, setOpened] = useState(false);

  useMultiSelectListboxMultiselectable(id, opened);

  return (
    <MultiSelect
      id={id}
      data={options}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      ariaDescribedBy={ariaDescribedBy}
      valid={!errorMessage}
      validationMessage={errorMessage}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {selected.length > 0 && (
            <MultiSelectClearButton
              onClear={() =>
                handleChange({ value: [] } as MultiSelectChangeEvent)
              }
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

export function STFFWizardForm({
  form,
  isNewRecord,
  lockedFields,
}: {
  form: UseFormReturn<STFF, any, STFF>;
  isNewRecord: boolean;
  lockedFields: LockedFields;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = false;
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("STFF");

  const {
    control,
    register,
    setValue,
    formState: { errors },
    watch,
    trigger,
  } = form;

  const {
    getRaces,
    setRaces,
    getLanguages,
    setLanguages,
    validateWaiverField,
    validateWaiverIssuedDate,
    validateWaiverExpirationDate,
    validatePermitIssuedDate,
    validatePermitExpirationDate,
    validatePermitNumber,
    validatePermitType,
    validateCurrentEmploymentStartDate,
    validateOverallEmploymentStartDate,
    validateEffectiveStartDate,
  } = useSTFFFormLogic(form, undefined);

  const staffWaiver = watch("StaffWaiver");
  const programYear = watch("ProgramYear");
  const effectiveStartDate = watch("EffectiveStartDate");
  const permitNumber = watch("StaffPermitOrCredentialNumber");
  const resetKey = isNewRecord ? "new" : "edit";
  const isWaiverFieldsDisabled = viewOnly || staffWaiver === "N";

  const hasPermitNumber =
    permitNumber != null && String(permitNumber).trim() !== "";

  const staffEthnicity = useWatch({
    control,
    name: "StaffEthnicity",
  });

  useEffect(() => {
    if (effectiveStartDate && programYear) {
      trigger("EffectiveStartDate");
    }
  }, [programYear, effectiveStartDate, trigger]);

  useEffect(() => {
    if (staffEthnicity === "Y" || staffEthnicity === "N") {
      setValue("StaffEthnicityMissingIndicator", "N", { shouldDirty: true });
    } else if (staffEthnicity === "") {
      setValue("StaffEthnicityMissingIndicator", "Y", { shouldDirty: true });
    }
  }, [staffEthnicity, setValue]);

  const languageOptions = (coreData.languagesList ?? []).map((x: any) => ({
    Text: x.Text ?? x.text ?? "",
    Value: x.Value ?? x.value ?? "",
  }));

  const selectedLanguages = getLanguages()
    .filter(Boolean)
    .map((val) => languageOptions.find((o: Option) => o.Value === val))
    .filter((o): o is Option => !!o);

  const handleLanguagesChange = (items: Option[]) => {
    const limited = items.slice(0, 5);
    setLanguages(limited.map((o) => o.Value));
    trigger("StaffLanguage1");
  };

  return (
    <Container
      className="form-style overview-container ps-3 pe-3"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUsers className="section-icon" />
          <h2 className="section-title">Staff Information</h2>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="LocalStaffId"
              labelText="Staff Local ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.staffLocalId}
                  maxLength={50}
                  {...register(id, { required: "Staff Local ID is required" })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="SEID"
              labelText="SEID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!isNewRecord}
                  maxLength={10}
                  {...register(id, {
                    validate: (value) => {
                      if (!value || value.trim() === "") return true;
                      if (value.length !== 10) {
                        return "SEID must be exactly 10 characters";
                      }
                      return true;
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Program Year is required" })}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    required: "Effective start date is required",
                    validate: validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUser className="section-icon" />
          <h2 className="section-title">Personal Information</h2>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLegalFirstName"
              labelText="First Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.firstName}
                  maxLength={30}
                  {...register(id, { required: "First name is required" })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLegalMiddleName"
              labelText="Middle Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(id)}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLegalLastName"
              labelText="Last Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.lastName}
                  maxLength={50}
                  {...register(id, { required: "Last name is required" })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffDateOfBirth"
              labelText="Date of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                lockedFields.dateOfBirth && !viewOnly ? (
                  <ReadOnlyDateInput
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Date of birth is required" }}
                  />
                ) : (
                  <DateInput
                    id={id}
                    disabled={viewOnly}
                    maxLength={50}
                    {...register(id, {
                      required: "Date of birth is required",
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffGender"
              labelText="Gender"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Gender is required" })}
                  options={coreData.genderList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffEthnicity"
              labelText="Hispanic"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
              labelAs="legend"
            >
              {(id, name, validationProps) => (
                <div>
                  <RadioSelect
                    id={id}
                    disabled={viewOnly}
                    {...validationProps}
                    {...register(name, {
                      required: "Hispanic field is required",
                    })}
                    options={[
                      { Text: "Yes", Value: "Y" },
                      { Text: "No", Value: "N" },
                      { Text: "No Response", Value: "" },
                    ]}
                  />
                  <input
                    type="hidden"
                    {...register("StaffEthnicityMissingIndicator")}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffRace1"
              labelText="Race(s)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <div>
                  <input type="hidden" {...register("StaffRace1")} />
                  <input type="hidden" {...register("StaffRace2")} />
                  <input type="hidden" {...register("StaffRace3")} />
                  <input type="hidden" {...register("StaffRace4")} />
                  <input type="hidden" {...register("StaffRace5")} />
                  <input
                    type="hidden"
                    {...register("StaffRaceMissingIndicator")}
                  />
                  <MultiSelectField
                    id={id}
                    key={`${resetKey}:races`}
                    disabled={viewOnly}
                    resetKey={resetKey}
                    options={coreData.raceList}
                    initialValues={getRaces()}
                    maxSelections={5}
                    placeholder="Select up to five races"
                    onChangeValues={setRaces}
                    ariaLabel="Race(s)"
                    ariaDescribedBy={`${id}-description`}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLanguage1"
              labelText="Language(s) Proficient"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <div>
                  <input
                    type="hidden"
                    {...register("StaffLanguage1", {
                      required: "At least one language is required",
                    })}
                  />
                  <input type="hidden" {...register("StaffLanguage2")} />
                  <input type="hidden" {...register("StaffLanguage3")} />
                  <input type="hidden" {...register("StaffLanguage4")} />
                  <input type="hidden" {...register("StaffLanguage5")} />
                  <LanguageMultiSelect
                    id={id}
                    disabled={viewOnly}
                    required
                    invalid={!!validationProps["aria-invalid"]}
                    data={languageOptions}
                    value={selectedLanguages}
                    onChange={handleLanguagesChange}
                    placeholder="Select up to five languages"
                    ariaLabel="Language(s) Proficient"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffHighestDegreeCode"
              labelText="Highest Education Level"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    required: "Staff highest degree is required",
                  })}
                  options={coreData.educationList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <LuBuilding2 className="section-icon" />
          <h2 className="section-title">Employment Information</h2>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffWaiver"
              labelText="Waiver?"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              labelAs="legend"
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffWaiverIssuedDate"
              labelText="Waiver Issued Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={staffWaiver === "Y"}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={isWaiverFieldsDisabled}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateWaiverIssuedDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffWaiverExpirationDate"
              labelText="Waiver Expiration Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={staffWaiver === "Y"}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={isWaiverFieldsDisabled}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateWaiverExpirationDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="StaffWaiverType"
              labelText="Waiver Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={staffWaiver === "Y"}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={isWaiverFieldsDisabled}
                  {...register(id, {
                    validate: (value) =>
                      validateWaiverField(value, "Waiver Type"),
                  })}
                  options={coreData.waiverTypes}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="StaffPermitOrCredentialType"
              labelText="Permit/Credential Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={hasPermitNumber}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    validate: validatePermitType,
                  })}
                  options={coreData.staffPermitList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffPermitOrCredentialNumber"
              labelText="Permit/Credential Number"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={15}
                  {...register(id, {
                    validate: validatePermitNumber,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffPermitIssuedDate"
              labelText="Permit/Credential Issued Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={hasPermitNumber}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validatePermitIssuedDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffPermitExpirationDate"
              labelText="Permit/Credential Expiration Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={hasPermitNumber}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validatePermitExpirationDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffCurrentEmploymentStartDate"
              labelText="Current Employment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateCurrentEmploymentStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="StaffCurrentEmploymentEndDate"
              labelText="Current Employment End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, { validate: validateMinDate })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffOverallEmploymentStartDate"
              labelText="Overall Employment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateOverallEmploymentStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
